/**
 * 🔒 LIBRARY SEAL — DO NOT MODIFY (Feb 24 2026). See /app/memory/LIBRARY_SEAL.md.
 * Sealed until the user types "library upgrade".
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Film, Headphones, BookOpen } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import BookCover from './BookCover';

/**
 * "Just added" shelf — content uploaded in the last 7 days.
 * Hidden when empty. Respects Early-Access gating server-side.
 */
const JustAddedShelf = ({ days = 7, limit = 12 }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(
          `${BACKEND_URL}/api/library/recent?days=${days}&limit=${limit}`
        );
        if (!r.ok) return;
        const { items: list } = await r.json();
        if (!cancelled) setItems(Array.isArray(list) ? list : []);
      } catch {
        /* noop */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openItem = (item) => {
    if (item.is_series) {
      // Jump to the library (series tab) so the user can pick an episode
      navigate('/library');
      return;
    }
    if (item.type === 'ebook' || item.type === 'text') {
      navigate(`/reader/${item.book_id}`);
    } else {
      navigate(`/player/${item.book_id}`);
    }
  };

  if (loading) return null;
  if (!items.length) return null;

  return (
    <section className="mb-6 sm:mb-8" data-testid="just-added-shelf">
      <div className="flex items-end justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-fuchsia-400 to-pink-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/30">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-white text-lg sm:text-xl font-bold tracking-tight leading-tight">
              Just added
            </h2>
            <p className="text-gray-400 text-xs">
              Fresh uploads from the last {days} days
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/library')}
          className="text-xs font-semibold text-indigo-300 hover:text-indigo-200 transition-colors"
          data-testid="just-added-see-all"
        >
          See all →
        </button>
      </div>

      <div
        className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 -mx-1 px-1 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-slate-700"
        data-testid="just-added-scroller"
      >
        {items.map((item) => {
          const Icon =
            item.type === 'cinematic'
              ? Film
              : item.type === 'audiobook'
              ? Headphones
              : BookOpen;
          return (
            <button
              key={item.book_id}
              onClick={() => openItem(item)}
              className="group relative shrink-0 w-36 sm:w-40 snap-start text-left rounded-xl overflow-hidden bg-slate-800/60 border border-slate-700/60 hover:border-fuchsia-500/60 transition-all shadow-md hover:shadow-fuchsia-500/10"
              data-testid={`just-added-item-${item.book_id}`}
            >
              <div className="relative w-full aspect-[2/3] bg-slate-900 overflow-hidden">
                <BookCover
                  book={item}
                  className="h-full w-full"
                  imageClassName="transition-transform duration-500 group-hover:scale-105 motion-reduce:transform-none"
                />
                {/* New badge */}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-white bg-fuchsia-500/90 backdrop-blur px-2 py-0.5 rounded-full shadow-md">
                  New
                </span>
                {/* Type pill */}
                <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-semibold text-white bg-black/60 backdrop-blur px-2 py-0.5 rounded-full">
                  <Icon className="w-3 h-3" />
                  {item.type}
                </span>
                {/* Episode count for series */}
                {item.is_series && (
                  <span className="absolute bottom-2 right-2 text-[10px] font-semibold text-white bg-black/70 backdrop-blur px-2 py-0.5 rounded">
                    {item.episode_count || 0} eps
                  </span>
                )}
              </div>
              <div className="p-2.5">
                <p
                  className="text-sm font-semibold text-white line-clamp-2 leading-snug"
                  title={item.title}
                >
                  {item.title}
                </p>
                {item.author && (
                  <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                    {item.author}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default JustAddedShelf;
